/**
 * Host-lib rationale: this dialog was extracted out of `apps/principle`
 * (`patients/patient-account/account-tab/account-credits/`) so that it can be
 * reused from the appointment surfaces as well as the patient account tab.
 *
 * It is hosted in `ng-payments` rather than `ng-principle-accounting` because
 * `ng-payments` already imports `ng-principle-accounting`
 * (see `libs/ng-payments/src/lib/ng-payments.module.ts` — the
 * `NgPrincipleAccountingModule` import). Hosting the dialog in
 * `ng-principle-accounting` would require that library to depend on
 * `ng-payments` (for `TransactionProviders` and the provider-option consts),
 * creating a circular project graph. Lint boundaries permit either pairing, but
 * the Nx project graph does not — `nx run ng-payments:lint` is the proof.
 */
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Money } from '@principle-theorem/accounting';
import { InvoiceActionService } from '@principle-theorem/ng-principle-accounting';
import {
  GlobalStoreService,
  OrganisationService,
  TreatmentCategorySelectorBloc,
} from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  TypedFormControl,
  isDisabled$,
} from '@principle-theorem/ng-shared';
import {
  AccountCredit,
  Invoice,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  AccountCreditType,
  type IAccountCredit,
  type IAppointment,
  type IBrand,
  type IInvoice,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITransaction,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import {
  snapshot,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { ReplaySubject, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AccountCreditCapture,
  AccountCreditCaptureStatus,
  type AccountCreditCaptureOutcome,
} from './account-credit-capture';
import {
  isResolvedTransactionOption,
  type IResolvedTransactionOption,
} from '../transaction-providers/transaction-provider';
import { HICAPS_CONNECT_PAYMENT_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/hicaps-connect-transaction-provider-options';
import { MEDIPASS_VIRTUAL_TERMINAL_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/medipass-transaction-provider-options';
import {
  CASH_PROVIDER_OPTION,
  MANUAL_PROVIDER_OPTION,
  STRIPE_PROVIDER_OPTION,
} from '../transaction-providers/transaction-provider-options/transaction-provider-options';
import { TYRO_PURCHASE_PROVIDER_OPTION } from '../transaction-providers/transaction-provider-options/tyro-transaction-provider-options';
import { TransactionProviders } from '../transaction-providers/transaction-providers.service';
import {
  AccountCreditFormGroup,
  type CreditOption,
  type ICreditOptionGroup,
} from './add-account-credit-form';

export interface IAddAccountCreditDialogData {
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  brand: WithRef<IBrand>;
  depositContext?: IAppointmentDepositContext;
}

export interface IAppointmentDepositContext {
  appointmentRef: DocumentReference<IAppointment>;
  amountCents: number;
  canOverrideAmount: boolean;
  defaultDescription: string;
  practitioner?: INamedDocument<IStaffer>;
  treatmentCategoryRef?: DocumentReference<ITreatmentCategory>;
  acquireInvoiceClaim: () => Promise<void>;
  abandonInvoiceClaim?: () => Promise<void>;
}

export interface IAppointmentDepositResult {
  invoice: WithRef<IInvoice>;
  transaction: DocumentReference<ITransaction>;
  amountCents: number;
}

export interface IAppointmentDepositErrorResult {
  readonly status: 'error';
  readonly error: unknown;
}

export type AppointmentDepositDialogResult =
  | IAppointmentDepositResult
  | IAppointmentDepositErrorResult;

@Component({
  selector: 'pr-add-account-credit-dialog',
  templateUrl: './add-account-credit-dialog.component.html',
  styleUrls: ['./add-account-credit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddAccountCreditDialogComponent {
  trackByGroup = TrackByFunctions.label<ICreditOptionGroup>();
  trackByOption = TrackByFunctions.label<CreditOption>();
  treatmentCategoryFormCtrl = new TypedFormControl<
    DocumentReference<ITreatmentCategory>
  >();
  accountCreditForm: AccountCreditFormGroup;
  optionGroups$: Observable<ICreditOptionGroup[]>;
  selectedPractice$ = new ReplaySubject<WithRef<IPractice>>(1);
  isDisabled$: Observable<boolean>;
  // Bound to the practitioner picker only: it filters selectable staff. Recording
  // deliberately carries no new actor permission gate and retains the status quo.
  permissions = [PatientPermissions.ReceiveDeposits];
  treatmentCategoryBloc: TreatmentCategorySelectorBloc;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IAddAccountCreditDialogData,
    public organisation: OrganisationService,
    private _dialogRef: MatDialogRef<
      AddAccountCreditDialogComponent,
      AppointmentDepositDialogResult
    >,
    private _transactions: TransactionProviders,
    private _invoiceActions: InvoiceActionService,
    private _globalStore: GlobalStoreService
  ) {
    this.accountCreditForm = new AccountCreditFormGroup(
      this.treatmentCategoryFormCtrl
    );
    if (this.data.depositContext) {
      const context = this.data.depositContext;
      this.accountCreditForm.patchValue({
        description: context.defaultDescription,
        amount: Money.fromCents(context.amountCents),
        reservedFor: {
          practitioner: context.practitioner,
          treatmentCategory: context.treatmentCategoryRef,
          appointmentRef: context.appointmentRef,
        },
      });
      if (!context.canOverrideAmount) {
        this.accountCreditForm.controls.amount.disable();
      }
    }
    const depositOptions$ = this._transactions.resolveProviders$([
      CASH_PROVIDER_OPTION,
      MANUAL_PROVIDER_OPTION,
      MEDIPASS_VIRTUAL_TERMINAL_PROVIDER_OPTION,
      STRIPE_PROVIDER_OPTION,
      TYRO_PURCHASE_PROVIDER_OPTION,
      HICAPS_CONNECT_PAYMENT_PROVIDER_OPTION,
    ]);
    this.optionGroups$ = depositOptions$.pipe(
      map((options) => [{ label: 'Deposit', options }])
    );

    this.selectedPractice$.next(this.data.practice);

    this.isDisabled$ = isDisabled$(this.accountCreditForm);

    this.treatmentCategoryBloc = new TreatmentCategorySelectorBloc(
      this._globalStore.treatmentCategories$,
      this.treatmentCategoryFormCtrl
    );
  }

  async submit(): Promise<void> {
    if (!this.accountCreditForm.valid) {
      return;
    }

    const formData = this.accountCreditForm.getRawValue();
    const resolvedMethod = isResolvedTransactionOption(formData.method)
      ? formData.method
      : undefined;
    if (this.data.depositContext && !resolvedMethod) {
      return;
    }
    const credit = AccountCredit.init({
      type: AccountCreditType.Deposit,
      description: formData.description,
      amount: formData.amount,
      reservedFor: {
        practitioner: formData.reservedFor.practitioner
          ? stafferToNamedDoc(formData.reservedFor.practitioner)
          : undefined,
        treatment: formData.reservedFor.treatment,
        appointmentRef: formData.reservedFor.appointmentRef,
        treatmentCategory: formData.reservedFor.treatmentCategory,
      },
      practiceRef: this.data.practice.ref,
    });

    if (this.data.depositContext && resolvedMethod) {
      try {
        const outcome = await this._handleDeposit(resolvedMethod, credit);
        if (outcome.status !== AccountCreditCaptureStatus.Captured) {
          return;
        }
        this._dialogRef.close({
          invoice: outcome.invoice,
          transaction: outcome.transaction,
          amountCents: Money.toCents(formData.amount),
        } satisfies IAppointmentDepositResult);
      } catch (error) {
        this._dialogRef.close({
          status: 'error',
          error,
        } satisfies IAppointmentDepositErrorResult);
      }
      return;
    }

    this._dialogRef.close();
    if (resolvedMethod) {
      await this._handleDeposit(resolvedMethod, credit);
      return;
    }
    await Invoice.addAccountCredit(this.data.patient, credit);
  }

  private async _handleDeposit(
    option: IResolvedTransactionOption,
    credit: IAccountCredit
  ): Promise<AccountCreditCaptureOutcome> {
    const staffer = await snapshot(this._invoiceActions.staffer$);
    const practice = await snapshot(this.selectedPractice$);
    return AccountCreditCapture.capture({
      option,
      credit,
      patient: this.data.patient,
      practice,
      staffer,
      transactions: this._transactions,
      acquireInvoiceClaim: this.data.depositContext?.acquireInvoiceClaim,
      abandonInvoiceClaim: this.data.depositContext?.abandonInvoiceClaim,
    });
  }
}
